/**
 * @registry Result of a browser compatibility check.
 */
export interface BrowserCompatibilityResult {
  isCompatible: boolean;
  issue: 'incompatible-browser' | 'embedded-browser' | 'other' | 'compatible';
  details: string;
  browserInfo?: string;
}

/**
 * @registry Parsed browser name, version, and mobile/embedded flags from a user agent.
 */
export interface BrowserInfo {
  name: string;
  version: string;
  isMobile: boolean;
  isEmbedded: boolean;
  userAgent: string;
}

const parseUserAgent = (userAgent: string): BrowserInfo => {
  const ua = userAgent.toLowerCase();

  // Check for embedded browsers
  const isEmbedded =
    ua.includes('teams') ||
    ua.includes('fbav') || // Facebook
    ua.includes('fban') || // Facebook
    ua.includes('gmail') ||
    ua.includes('slack') ||
    ua.includes('outlook') ||
    ua.includes('yahoo') ||
    ua.includes('webview') ||
    ua.includes('; wv') ||
    (ua.includes('mobile safari') &&
      (ua.includes('crios') || ua.includes('fxios') || ua.includes('opios')));

  // Check for mobile browsers (excluding embedded browsers)
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua) &&
    !isEmbedded;

  // Detect browser and version
  let name = 'Unknown';
  let version = 'Unknown';

  // Internet Explorer
  if (/msie|trident/i.test(ua)) {
    name = 'Internet Explorer';
    if (/msie (\d+)/i.test(ua)) {
      version = RegExp.$1;
    } else if (/trident\/(\d+)/i.test(ua)) {
      const tridentVersion = parseInt(RegExp.$1);
      // Map Trident version to IE version
      const ieVersionMap: {[key: number]: string} = {
        4: '8',
        5: '9',
        6: '10',
        7: '11',
      };
      version = ieVersionMap[tridentVersion] || 'Unknown';
    }
  }
  // Edge (Chromium-based)
  else if (/edg\/(\d+)/i.test(ua)) {
    name = 'Edge';
    version = RegExp.$1;
  }
  // Edge (Legacy)
  else if (/edge\/(\d+)/i.test(ua)) {
    name = 'Edge (Legacy)';
    version = RegExp.$1;
  }
  // Chrome
  else if (/chrome\/(\d+)/i.test(ua)) {
    name = 'Chrome';
    version = RegExp.$1;
  }
  // Firefox
  else if (/firefox\/(\d+)/i.test(ua)) {
    name = 'Firefox';
    version = RegExp.$1;
  }
  // Safari
  else if (/safari\/(\d+)/i.test(ua) && !/chrome/i.test(ua)) {
    name = 'Safari';
    version = RegExp.$1;
  }
  // Opera
  else if (/opera\/(\d+)/i.test(ua)) {
    name = 'Opera';
    version = RegExp.$1;
  } else if (/opr\/(\d+)/i.test(ua)) {
    name = 'Opera';
    version = RegExp.$1;
  }

  return {
    name,
    version,
    isMobile,
    isEmbedded,
    userAgent,
  };
};

const isBrowserVersionSupported = (browserInfo: BrowserInfo): boolean => {
  const version = parseInt(browserInfo.version);

  if (isNaN(version)) {
    return false;
  }

  const minVersions: {[key: string]: number} = {
    Chrome: 80,
    Firefox: 75,
    Safari: 13,
    Edge: 79,
    'Edge (Legacy)': 79, // Legacy Edge is not supported
    Opera: 67,
  };

  const minVersion = minVersions[browserInfo.name];
  return minVersion ? version >= minVersion : false;
};

/**
 * @registry Parses the current browser's user agent into name/version/mobile/embedded info.
 */
export const getBrowserInfo = (): BrowserInfo =>
  parseUserAgent(navigator.userAgent);

/**
 * @registry Checks the current browser for embedded, missing-feature, or unsupported-version issues.
 */
export const checkBrowserCompatibility = (): BrowserCompatibilityResult => {
  // Parse user agent and check browser compatibility
  const userAgent = navigator.userAgent;
  const browserInfo = parseUserAgent(userAgent);

  // Check for embedded browsers first.
  if (browserInfo.isEmbedded) {
    return {
      isCompatible: false,
      issue: 'embedded-browser',
      details: 'Embedded browser detected',
      browserInfo: `${browserInfo.name} ${browserInfo.version} (embedded)`,
    };
  }

  // Check for required browser features
  const requiredFeatures = [
    'localStorage',
    'sessionStorage',
    'fetch',
    'Promise',
  ];

  // Check if all required features are available
  for (const feature of requiredFeatures) {
    if (!window[feature as keyof Window]) {
      return {
        isCompatible: false,
        issue: 'incompatible-browser',
        details: `Missing required feature: ${feature}`,
        browserInfo: `Missing: ${feature}`,
      };
    }
  }

  // Check for modern browser APIs
  if (!window.fetch) {
    return {
      isCompatible: false,
      issue: 'incompatible-browser',
      details: 'Fetch API not supported',
      browserInfo: 'Fetch API not available',
    };
  }

  // Check for Internet Explorer (any version is incompatible)
  if (browserInfo.name === 'Internet Explorer') {
    return {
      isCompatible: false,
      issue: 'incompatible-browser',
      details: 'Internet Explorer is not supported',
      browserInfo: `${browserInfo.name} ${browserInfo.version}`,
    };
  }

  // Check for old Edge versions
  if (
    browserInfo.name === 'Edge (Legacy)' &&
    parseInt(browserInfo.version) < 79
  ) {
    return {
      isCompatible: false,
      issue: 'incompatible-browser',
      details: 'Old Edge version detected',
      browserInfo: `${browserInfo.name} ${browserInfo.version}`,
    };
  }

  // Check for unsupported browser versions
  if (!isBrowserVersionSupported(browserInfo)) {
    return {
      isCompatible: false,
      issue: 'incompatible-browser',
      details: 'Unsupported browser version',
      browserInfo: `${browserInfo.name} ${browserInfo.version}`,
    };
  }

  return {
    isCompatible: true,
    issue: 'compatible',
    details: 'Browser is compatible',
    browserInfo: `${browserInfo.name} ${browserInfo.version}`,
  };
};
